import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { crispService } from "@services/crisp/crispService";
import { v4 as uuidv4 } from "uuid";

const CRISP_TOKEN_KEY = "crisp_user_token";
const CRISP_HIDDEN_EXACT_ROUTES = [
  "/delete-modal",
  "/remove-modal",
  "/unlink-modal",
  "/batch-items-menu",
  "/batch-unlink-items",
  "/board-item-menu",
  "/boards-dialog",
  "/free-month-offer",
];
const CRISP_HIDDEN_ROUTE_PREFIXES = ["/settings", "/sync-wizard"];

const normalizePathname = (pathname: string): string => {
  if (!pathname || pathname === "/") {
    return "/";
  }

  return pathname.endsWith("/") ? pathname.slice(0, -1) : pathname;
};

const isHiddenByPrefix = (pathname: string): boolean =>
  CRISP_HIDDEN_ROUTE_PREFIXES.some(
    (routePrefix) => pathname === routePrefix || pathname.startsWith(`${routePrefix}/`),
  );

export const isCrispHiddenRoute = (pathname: string): boolean =>
  CRISP_HIDDEN_EXACT_ROUTES.includes(normalizePathname(pathname)) ||
  isHiddenByPrefix(normalizePathname(pathname));

export function setCrispUserData(
  userId: number | null | undefined,
  accountId: number | null | undefined,
): void {
  if (!userId) return;

  // accountId is passed through even if absent; userId alone is sufficient for Mixpanel correlation

  crispService.setSessionData({ monday_user_id: userId, monday_account_id: accountId });
}

/**
 * Hook to manage Crisp chat visibility based on current route.
 * Shows chat on all routes except those explicitly hidden.
 * Attaches Monday user identity to the Crisp contact profile for analytics correlation.
 */
export const useCrispChat = (userId?: number | null, accountId?: number | null) => {
  const { pathname } = useLocation();
  const shouldHideChat = isCrispHiddenRoute(pathname);
  const showChat = !shouldHideChat;

  useEffect(() => {
    if (showChat) {
      const existingToken = localStorage.getItem(CRISP_TOKEN_KEY);
      const token = existingToken || uuidv4();

      if (!existingToken) {
        localStorage.setItem(CRISP_TOKEN_KEY, token);
      }

      crispService.setTokenId(token);
      setCrispUserData(userId, accountId);
      crispService.showChat();
      crispService.enableMinimizedDim();
    } else {
      crispService.hideChat();
    }
  }, [showChat, userId, accountId]);
};

export default useCrispChat;
